// ============================================================================
// CHANGO V5.0 — Email Verification Page (T-D1-021)
// ============================================================================
// Six-digit code entry, auto-submit when all six are filled, 60-second resend
// cooldown. Reads `email` from location.state when arriving from Register.
// In dev the backend accepts hardcoded `123456` — note shown in the UI.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';

const COOLDOWN_SECONDS = 60;

function CodeInput({ value, onChange, disabled }) {
  // Six independent input cells with auto-focus stepping. Pasting 6 digits
  // into the first cell distributes across all six.
  const refs = useRef([]);

  const setDigit = (idx, char) => {
    const next = value.split('');
    next[idx] = char;
    while (next.length < 6) next.push('');
    onChange(next.slice(0, 6).join(''));
  };

  const handleChange = (idx, e) => {
    const v = e.target.value.replace(/\D/g, '').slice(-1);
    if (!v) {
      setDigit(idx, '');
      return;
    }
    setDigit(idx, v);
    if (idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.padEnd(6, '').slice(0, 6));
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length: 6 }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => { refs.current[idx] = el; }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={value[idx] || ''}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          className="h-12 w-12 rounded-md border border-slate-300 text-center text-xl font-semibold focus:border-chango-500 focus:outline-none focus:ring-2 focus:ring-chango-200"
        />
      ))}
    </div>
  );
}

export default function EmailVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (theCode) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setServerError(null);
    try {
      await api.post('/api/auth/verify', { email, verification_code: theCode });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      submittedRef.current = false;
      setServerError(err instanceof HttpError ? err.message : '验证失败');
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit when six digits are present.
  useEffect(() => {
    if (code.length === 6 && /^\d{6}$/.test(code) && email) {
      submit(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const requestResend = async () => {
    if (cooldown > 0 || !email) return;
    setServerError(null);
    try {
      await api.post('/api/auth/verify/resend', { email });
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      setServerError(err instanceof HttpError ? err.message : '发送失败');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-xl font-semibold text-slate-800">验证邮箱</h1>
        <p className="mt-2 text-sm text-slate-600">
          {email
            ? <>我们已向 <span className="font-medium text-slate-800">{email}</span> 发送 6 位验证码，请在下方输入。</>
            : '请输入您邮箱中收到的 6 位验证码。'}
        </p>

        <div className="mt-6">
          <CodeInput value={code} onChange={setCode} disabled={submitting || success} />
        </div>

        {success ? (
          <div role="status" className="alert-info mt-6 text-center">邮箱验证成功，正在跳转登录…</div>
        ) : null}

        {serverError ? (
          <div role="alert" className="alert-error mt-6">{serverError}</div>
        ) : null}

        <div className="mt-6 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={requestResend}
            disabled={cooldown > 0 || !email}
            className="text-chango-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-400"
          >
            {cooldown > 0 ? `${cooldown} 秒后可重新发送` : '重新发送验证码'}
          </button>
          <Link to="/login" className="text-slate-500 hover:text-slate-700">返回登录</Link>
        </div>

        {import.meta.env.DEV ? (
          <p className="mt-6 rounded bg-amber-50 p-2 text-xs text-amber-700">
            开发模式：输入 <code className="font-mono">123456</code> 可直接通过验证。
          </p>
        ) : null}
      </div>
    </div>
  );
}
